/**
 * The auto subtitle generator's chain (SEO_BRIEF_04 item 14), kept apart from the page around it
 * (pages/autosub.ts) so node can run it end to end:
 *
 *   an accepted upload  → its transcript         (`waitForTranscript`: at once, or by polling the job)
 *   the transcript      → timed cues in the worker (`cuesFromTranscript`: the chain the transcript's
 *                                                  own SRT download runs, pages/format.ts)
 *   the edited cues     → a subtitle file for the burn-in, with the video (`subtitlesForBurn`,
 *                                                  `handToBurn`: web/static/burn's two events)
 *
 * The video never appears in any request here: the upload is its audio (lib/video-flow.ts), and
 * the burn-in is handed the `File` itself, in this tab.
 */

import type { DocumentId, WriteFormat } from '../../../../../contracts/cue-model.ts';
import { resegmentOp } from '../../../../../src/resegment/index.ts';
import { getTranscript } from './api.ts';
import type { JobResponse, SubmitResponse } from './api-types.ts';
import type { CueEditorHandle } from './cue-editor.ts';
import { type PollHandle, pollJob } from './poll.ts';
import type { SubtitleWorkerClient } from './worker.ts';
import { downloadWriteOptions } from './write-options.ts';

export type TranscriptWait =
  | { readonly ok: true; readonly publicId: string }
  | { readonly ok: false; readonly state: string };

export interface TranscriptWaitHandle {
  readonly done: Promise<TranscriptWait>;
  /** A newer drop: stop polling. `done` then never settles. */
  stop(): void;
}

/** `/t/{id}` → `id`, for a job answer that names its transcript by URL only. */
export function publicIdOf(url: string | null | undefined): string | null {
  const m = url ? /^\/t\/([^/?#]+)/.exec(url) : null;
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

/**
 * The transcript an accepted upload leads to. A resolved answer names it; a queued one is polled
 * at the server's own pace (lib/poll.ts) until the job is done, or until it fails with the state
 * the job page would show (E5, E6, E12…).
 */
export function waitForTranscript(
  response: SubmitResponse,
  onJob?: (job: JobResponse) => void,
): TranscriptWaitHandle {
  if (response.resolved) {
    return { done: Promise.resolve({ ok: true, publicId: response.public_id }), stop() {} };
  }
  let handle: PollHandle | null = null;
  const done = new Promise<TranscriptWait>((resolve) => {
    handle = pollJob(response.job_id, {
      onUpdate: (job) => onJob?.(job),
      onTerminal: (job) => {
        const publicId = job.public_id ?? publicIdOf(job.transcript_url);
        if (job.status === 'done' && publicId) resolve({ ok: true, publicId });
        else resolve({ ok: false, state: job.state ?? job.error_code ?? 'E12' });
      },
      onError: (err) => {
        // Only a 404 is terminal; anything else is retried with backoff.
        if (err.problem.status === 404) resolve({ ok: false, state: 'E12' });
      },
    });
  });
  return { done, stop: () => handle?.stop() };
}

export interface OpenedTranscript {
  readonly docId: DocumentId;
  readonly cueCount: number;
  readonly language: string;
}

/**
 * The transcript as subtitle cues, held by the worker: the wire JSON is ingested there (both steps
 * mint ids, and the worker is the only id authority) and `resegment` turns Whisper's segments into
 * lines of subtitle length, with the language's own line-break rules. Exactly what /t/{id}/srt
 * writes, so the editor opens on the subtitles the SRT download would have given.
 */
export async function cuesFromTranscript(
  client: Pick<SubtitleWorkerClient, 'request'>,
  publicId: string,
  inputName: string,
): Promise<OpenedTranscript> {
  const wire = await getTranscript(publicId);
  const ingested = await client.request('ingestTranscript', { wire, inputName });
  const segmented = await client.request('resegment', {
    docId: ingested.docId,
    op: resegmentOp(ingested.trackId, ingested.language),
  });
  return { docId: segmented.docId, cueCount: segmented.cueCount, language: ingested.language };
}

/** What the burn-in is given: SRT, or ASS for a file that came with styles of its own. */
export function burnFormat(sourceFormat: string | null): Extract<WriteFormat, 'srt' | 'ass'> {
  return sourceFormat === 'ass' || sourceFormat === 'ssa' ? 'ass' : 'srt';
}

/** `holiday.mp4` → `holiday`, for the names of the files the page makes. */
export function baseName(name: string): string {
  return name.replace(/\.[^./\\]+$/, '').trim() || 'subtitles';
}

export interface BurnSubtitles {
  readonly bytes: Uint8Array;
  readonly name: string;
}

/** The edited cues, written as the burn-in reads them (web/static/burn/src/lib/read.ts). */
export async function subtitlesForBurn(
  editor: Pick<CueEditorHandle, 'write'>,
  format: Extract<WriteFormat, 'srt' | 'ass'>,
  base: string,
): Promise<BurnSubtitles> {
  const payload = await editor.write(format, downloadWriteOptions(format));
  const bytes =
    typeof payload.output === 'string' ? new TextEncoder().encode(payload.output) : payload.output;
  return { bytes, name: `${base}.${format}` };
}

/** The video and the subtitles to the burn-in mounted on `root` (`[data-burn-page]`). */
export function handToBurn(root: EventTarget, video: File, subtitles: BurnSubtitles): void {
  root.dispatchEvent(new CustomEvent('burn:video', { detail: { file: video } }));
  root.dispatchEvent(new CustomEvent('burn:subtitles', { detail: subtitles }));
}
